import { Button } from '@frontend-platform/ui-lib/ui';
import { Download, FolderOpen } from 'lucide-react';
import { useMemo, useState } from 'react';
import Markdown from 'react-markdown';
import { toast } from 'sonner';
import { getPresignedReportDownloadUrl } from '../../api/api';
import { BandBadge } from './band-badge';
import { IMPACT_BAND_ORDER, normalizeBand } from './band-config';
import { SuggestionCard } from './suggestion-card';
import type { CategorySummary, MetricSummary, RecommendationItem, RunDetail } from './types';

// Triggers a browser download for a (presigned) URL via a transient anchor —
// same mechanism agent-tools' `triggerBrowserDownload` uses.
function triggerBrowserDownload(url: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener';
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

interface AnalysisPanelProps {
  runDetails: RunDetail | undefined;
  selectedCategory: string | null;
  selectedMetric: string | null;
}

function rankBand(band: string | undefined): number {
  const idx = IMPACT_BAND_ORDER.indexOf(normalizeBand(band) as (typeof IMPACT_BAND_ORDER)[number]);
  return idx === -1 ? 99 : idx;
}

// Right-panel view shown when a category or metric is selected. Mirrors
// aistudio's Analysis.vue: header with band + report/conversation stats, an
// optional markdown analysis summary, and the filtered recommendation list.
export function AnalysisPanel({
  runDetails,
  selectedCategory,
  selectedMetric,
}: AnalysisPanelProps) {
  const isMetric = !!selectedMetric;
  const [isDownloading, setIsDownloading] = useState(false);

  async function handleDownloadReport(reportDownloadUrl: string) {
    setIsDownloading(true);
    try {
      const presignedUrl = await getPresignedReportDownloadUrl(reportDownloadUrl);
      triggerBrowserDownload(presignedUrl);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to download report');
    } finally {
      setIsDownloading(false);
    }
  }

  const selectedItem = useMemo<CategorySummary | MetricSummary | null>(() => {
    if (selectedMetric) {
      return runDetails?.metricSummaries?.find((m) => m.metricId === selectedMetric) ?? null;
    }
    return (
      runDetails?.overallEval?.categorySummaries?.find((c) => c.category === selectedCategory) ??
      null
    );
  }, [runDetails, selectedCategory, selectedMetric]);

  const filteredItems = useMemo<RecommendationItem[]>(() => {
    const items = runDetails?.items ?? [];
    let result: RecommendationItem[];
    if (selectedMetric) {
      result = items.filter((item) => item.contributingMetrics?.includes(selectedMetric));
    } else if (selectedCategory) {
      const metricIds =
        runDetails?.metricSummaries
          ?.filter((m) => m.category === selectedCategory)
          .map((m) => m.metricId) ?? [];
      result = items.filter((item) =>
        item.contributingMetrics?.some((id) => metricIds.includes(id))
      );
    } else {
      result = items;
    }
    return [...result].sort((a, b) => rankBand(a.impactBand) - rankBand(b.impactBand));
  }, [runDetails, selectedCategory, selectedMetric]);

  if (!selectedItem) {
    return null;
  }

  const metric = isMetric ? (selectedItem as MetricSummary) : null;
  const category = !isMetric ? (selectedItem as CategorySummary) : null;
  const title = category?.categoryLabel ?? metric?.metricName ?? '';
  const summary = selectedItem.summary || selectedItem.analysisSummary || '';

  return (
    <div className="h-full overflow-auto rounded-lg border border-border bg-background">
      <div className="flex h-11 items-center justify-between border-border border-b px-6">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-base text-foreground">{title}</span>
          <BandBadge band={selectedItem.band} />
        </div>
        {!isMetric && category?.reportDownloadUrl ? (
          <Button
            size="sm"
            disabled={isDownloading}
            onClick={() => handleDownloadReport(category.reportDownloadUrl as string)}
          >
            <Download className="h-3.5 w-3.5" />
            {isDownloading ? 'Preparing…' : 'Download Report'}
          </Button>
        ) : isMetric && metric ? (
          <div className="flex items-center gap-2 text-muted-foreground text-xs">
            <span className="rounded-full bg-muted px-2 py-0.5">
              Conversations - {metric.conversationsAnalyzed ?? 0}
            </span>
            <span className="rounded-full bg-muted px-2 py-0.5">
              Issues - {metric.conversationsWithIssues ?? 0}
            </span>
          </div>
        ) : null}
      </div>

      {summary && (
        <div className="border-border border-b">
          <div className="flex h-10 items-center border-border border-b px-6 font-semibold text-muted-foreground text-sm">
            Analysis
          </div>
          <div className="prose prose-sm max-w-none px-6 py-4 text-foreground text-sm">
            <Markdown>{summary}</Markdown>
          </div>
        </div>
      )}

      <div>
        <div className="flex h-10 items-center px-6 font-semibold text-muted-foreground text-sm">
          Recommendations
          <span className="ml-1 font-normal">({filteredItems.length})</span>
        </div>
        {filteredItems.length > 0 ? (
          <div className="space-y-3 border-border border-t px-6 py-4">
            {filteredItems.map((item) => (
              <SuggestionCard key={item.issueId} item={item} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 border-border border-t py-12 text-center">
            <FolderOpen className="h-5 w-5 text-muted-foreground" />
            <div className="font-medium text-foreground text-sm">No Recommendations</div>
            <div className="text-muted-foreground text-sm">
              No recommendations have been generated.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
